<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const props = defineProps({
  criterion: { type: Object, default: null },
});

const emit = defineEmits(['saved']);

const { t } = useI18n();

const KINDS = ['score', 'boolean', 'enum', 'text'];
const SCORES = [1, 2, 3, 4, 5];

const dialogRef = ref(null);
const isSaving = ref(false);
const errorMessage = ref('');

const form = ref({
  name: '',
  description: '',
  kind: 'score',
  instruction: '',
  anchors: {},
  options: [],
  requireEvidence: true,
  active: true,
});

const optionsText = ref('');

const isEditing = computed(() => Boolean(props.criterion?.id));

const kindOptions = computed(() =>
  KINDS.map(kind => ({
    value: kind,
    label: t(`ANALYSIS.CRITERIA.KIND.${kind.toUpperCase()}`),
  }))
);

const kindHint = computed(() =>
  t(`ANALYSIS.CRITERIA.KIND_HINT.${form.value.kind.toUpperCase()}`)
);

// Evidence only makes sense for a judgement; a quote backing a free-text summary quotes the summary.
const supportsEvidence = computed(() =>
  ['score', 'boolean'].includes(form.value.kind)
);

const parsedOptions = computed(() =>
  optionsText.value
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean)
);

const isValid = computed(() => {
  if (!form.value.name.trim() || !form.value.instruction.trim()) return false;
  if (form.value.kind === 'enum') {
    return parsedOptions.value.length >= 2;
  }
  return true;
});

const resetForm = () => {
  const source = props.criterion;
  form.value = {
    name: source?.name ?? '',
    description: source?.description ?? '',
    kind: source?.kind ?? 'score',
    instruction: source?.instruction ?? '',
    anchors: { ...(source?.anchors ?? {}) },
    options: source?.options ?? [],
    requireEvidence: source?.require_evidence ?? true,
    active: source?.active ?? true,
  };
  optionsText.value = (source?.options ?? []).join('\n');
  errorMessage.value = '';
};

watch(() => props.criterion, resetForm, { immediate: true });

const payload = computed(() => ({
  name: form.value.name.trim(),
  description: form.value.description.trim(),
  kind: form.value.kind,
  instruction: form.value.instruction.trim(),
  anchors: form.value.kind === 'score' ? form.value.anchors : {},
  options: form.value.kind === 'enum' ? parsedOptions.value : [],
  require_evidence: supportsEvidence.value ? form.value.requireEvidence : false,
  active: form.value.active,
}));

const open = () => {
  resetForm();
  dialogRef.value.open();
};

const close = () => dialogRef.value.close();

const onConfirm = async () => {
  if (!isValid.value) return;
  isSaving.value = true;
  errorMessage.value = '';
  try {
    emit('saved', payload.value);
  } finally {
    isSaving.value = false;
  }
};

const setError = message => {
  errorMessage.value = message;
};

defineExpose({ open, close, setError });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="2xl"
    :title="
      isEditing
        ? t('ANALYSIS.CRITERIA.EDIT.TITLE')
        : t('ANALYSIS.CRITERIA.CREATE.TITLE')
    "
    :disable-confirm-button="!isValid || isSaving"
    :is-loading="isSaving"
    @confirm="onConfirm"
  >
    <div class="flex flex-col gap-4">
      <Input
        v-model="form.name"
        :label="t('ANALYSIS.CRITERIA.FORM.NAME')"
        :placeholder="t('ANALYSIS.CRITERIA.FORM.NAME_PLACEHOLDER')"
      />

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('ANALYSIS.CRITERIA.FORM.KIND') }}
        </label>
        <Select
          v-model="form.kind"
          :options="kindOptions"
          :disabled="isEditing"
        />
        <p class="text-sm text-n-slate-11">{{ kindHint }}</p>
        <p v-if="isEditing" class="text-sm text-n-slate-11">
          {{ t('ANALYSIS.CRITERIA.FORM.KEY_LOCKED') }}
        </p>
      </div>

      <TextArea
        v-model="form.instruction"
        :label="t('ANALYSIS.CRITERIA.FORM.INSTRUCTION')"
        :placeholder="t('ANALYSIS.CRITERIA.FORM.INSTRUCTION_PLACEHOLDER')"
        :max-length="1000"
        auto-height
      />

      <div v-if="form.kind === 'enum'" class="flex flex-col gap-1">
        <TextArea
          v-model="optionsText"
          :label="t('ANALYSIS.CRITERIA.FORM.OPTIONS')"
          :placeholder="t('ANALYSIS.CRITERIA.FORM.OPTIONS_PLACEHOLDER')"
          :max-length="1000"
          auto-height
        />
        <p class="text-sm text-n-slate-11">
          {{ t('ANALYSIS.CRITERIA.FORM.OPTIONS_HINT') }}
        </p>
      </div>

      <div v-if="form.kind === 'score'" class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('ANALYSIS.CRITERIA.FORM.ANCHORS') }}
        </label>
        <div
          v-for="score in SCORES"
          :key="score"
          class="flex items-center gap-3"
        >
          <span
            class="w-6 h-6 grid place-items-center rounded-md bg-n-alpha-2 text-sm text-n-slate-11 shrink-0"
          >
            {{ score }}
          </span>
          <Input
            v-model="form.anchors[String(score)]"
            size="sm"
            class="flex-1"
            :placeholder="
              t('ANALYSIS.CRITERIA.FORM.ANCHOR_PLACEHOLDER', { score })
            "
          />
        </div>
        <p class="text-sm text-n-slate-11">
          {{ t('ANALYSIS.CRITERIA.FORM.ANCHORS_HINT') }}
        </p>
      </div>

      <div v-if="supportsEvidence" class="flex items-start gap-3">
        <Switch v-model="form.requireEvidence" class="mt-0.5" />
        <div class="flex flex-col">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('ANALYSIS.CRITERIA.FORM.REQUIRE_EVIDENCE') }}
          </span>
          <span class="text-sm text-n-slate-11">
            {{ t('ANALYSIS.CRITERIA.FORM.REQUIRE_EVIDENCE_HINT') }}
          </span>
        </div>
      </div>

      <div class="flex items-center gap-3">
        <Switch v-model="form.active" />
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('ANALYSIS.CRITERIA.FORM.ACTIVE') }}
        </span>
      </div>

      <p v-if="errorMessage" class="text-sm text-n-ruby-11">
        {{ errorMessage }}
      </p>
    </div>
  </Dialog>
</template>
